import { DemoBanner } from '@/components/demo-banner';
import { ProtectedRoute } from '@/components/protected-route';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider } from '@/context/auth';
import { SettingsProvider } from '@/context/settings';
import { useTrafficLogger } from '@/hooks/use-traffic-logger';
import { isDemoMode } from '@/lib/demo-mode';
import AdminAnalytics from '@/pages/admin/analytics';
import AdminAppointments from '@/pages/admin/appointments';
import AdminContent from '@/pages/admin/content';
import AdminCourses from '@/pages/admin/courses';
import AdminDashboard from '@/pages/admin/dashboard';
import AdminLayout from '@/pages/admin/layout';
import AdminLeads from '@/pages/admin/leads';
import AdminMessages from '@/pages/admin/messages';
import AdminProposal from '@/pages/admin/proposal';
import AdminScheduling from '@/pages/admin/scheduling';
import AdminUsers from '@/pages/admin/users';
import Book from '@/pages/book';
import Dashboard from '@/pages/dashboard';
import Home from '@/pages/home';
import LoginPage from '@/pages/login';
import NotFound from '@/pages/not-found';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { setBaseUrl } from '@workspace/api-client-react';
import { Route, Switch, Router as WouterRouter } from 'wouter';

// Configure API base URL (only when explicitly set via env var)
// If not set, relative paths are used which works for same-domain deployments (Cloudflare Pages)
if (!isDemoMode && import.meta.env.VITE_API_URL) {
  setBaseUrl(import.meta.env.VITE_API_URL);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function Router() {
  useTrafficLogger();

  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/login" component={LoginPage} />
      <Route
        path="/dashboard"
        component={() => (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        )}
      />
      <Route path="/book" component={Book} />

      {/* Admin Routes */}
      <Route
        path="/admin"
        component={() => (
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          </ProtectedRoute>
        )}
      />
      <Route
        path="/admin/analytics"
        component={() => (
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminAnalytics />
            </AdminLayout>
          </ProtectedRoute>
        )}
      />
      <Route
        path="/admin/appointments"
        component={() => (
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminAppointments />
            </AdminLayout>
          </ProtectedRoute>
        )}
      />
      <Route
        path="/admin/messages"
        component={() => (
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminMessages />
            </AdminLayout>
          </ProtectedRoute>
        )}
      />
      <Route
        path="/admin/courses"
        component={() => (
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminCourses />
            </AdminLayout>
          </ProtectedRoute>
        )}
      />
      <Route
        path="/admin/content"
        component={() => (
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminContent />
            </AdminLayout>
          </ProtectedRoute>
        )}
      />
      <Route
        path="/admin/leads"
        component={() => (
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminLeads />
            </AdminLayout>
          </ProtectedRoute>
        )}
      />
      <Route
        path="/admin/users"
        component={() => (
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminUsers />
            </AdminLayout>
          </ProtectedRoute>
        )}
      />
      <Route
        path="/admin/scheduling"
        component={() => (
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminScheduling />
            </AdminLayout>
          </ProtectedRoute>
        )}
      />
      <Route
        path="/admin/proposal"
        component={() => (
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminProposal />
            </AdminLayout>
          </ProtectedRoute>
        )}
      />

      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SettingsProvider>
          <TooltipProvider>
            <DemoBanner />
            <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, '')}>
              <Router />
            </WouterRouter>
            <Toaster />
          </TooltipProvider>
        </SettingsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
